/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react-refresh/only-export-components */
import type { TypedMutationTrigger } from '@reduxjs/toolkit/dist/query/react'
import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState } from 'react'

import { useReportUrlMutation } from '@Store/api/product'

import {
  BaseModal,
  type BaseModalDispatch,
  BaseModalProvider,
  type BaseModalState,
  useBaseModalDispatch,
  useBaseModalState,
} from './BaseModal'

interface ReportUrlState extends BaseModalState {
  urlInfo?: { id: number; urlId: number }
  isSuccess: boolean
  isError: boolean
  isLoading: boolean
  error?: any
}

interface ReportUrlDispatch extends BaseModalDispatch {
  reportUrl: TypedMutationTrigger<any, any, any>
  resetReportUrl: () => void
  setUrlInfo: (payload: { id: number; urlId: number }) => void
}

const ReportUrlStateContext = createContext<ReportUrlState>(
  {} as ReportUrlState,
)
ReportUrlStateContext.displayName = 'ReportUrlState'

const ReportUrlDispatchContext = createContext<ReportUrlDispatch>(
  {} as ReportUrlDispatch,
)
ReportUrlDispatchContext.displayName = 'ReportUrlDispatch'

function ReportUrlProvider({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  return (
    <BaseModalProvider>
      <ReportUrlProviderInner modalContent={modalContent}>
        {children}
      </ReportUrlProviderInner>
    </BaseModalProvider>
  )
}

function ReportUrlProviderInner({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  const [urlInfo, setUrlInfo] = useState<{ id: number; urlId: number }>()

  const baseState = useBaseModalState()
  const baseDispatch = useBaseModalDispatch()

  const [
    reportUrl,
    { isSuccess, isError, error, isLoading, reset: resetReportUrl },
  ] = useReportUrlMutation()

  return (
    <ReportUrlStateContext.Provider
      value={{ ...baseState, urlInfo, isSuccess, isError, error, isLoading }}
    >
      <ReportUrlDispatchContext.Provider
        value={{ ...baseDispatch, setUrlInfo, reportUrl, resetReportUrl }}
      >
        <BaseModal isOpen={baseState.isOpen} close={baseDispatch.close}>
          {modalContent}
        </BaseModal>
        {children}
      </ReportUrlDispatchContext.Provider>
    </ReportUrlStateContext.Provider>
  )
}

function useReportUrlState(): ReportUrlState {
  try {
    const context = useContext(ReportUrlStateContext)
    return context
  } catch (error) {
    throw new Error('useReportUrlState must be used within a ReportUrlProvider')
  }
}

function useReportUrlDispatch(): ReportUrlDispatch {
  try {
    const context = useContext(ReportUrlDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useReportUrlDispatch must be used within a ReportUrlProvider',
    )
  }
}

export { ReportUrlProvider, useReportUrlState, useReportUrlDispatch }
